/**
 * Material Crafting Recipes
 * Refined materials and crafting components
 *
 * These create new materials used in higher-tier crafting recipes
 *
 * NOTE: All costs are 5x base for meaningful investment
 */

export const MATERIAL_RECIPES = {
  // ===========================================
  // TIER 2 - Basic Refined Materials (80% quality / 60s)
  // ===========================================
  craft_refined_leather: {
    id: 'craft_refined_leather',
    name: 'Refined Leather',
    icon: '🧳',
    category: 'materials',
    tier: 2,
    description: 'High-quality leather for advanced crafting.',
    cost: {
      resources: { gold: 75 },
      materials: { leather: 25, leatherScraps: 50 },
    },
    output: {
      type: 'material',
      itemId: 'refinedLeather',
      quantity: 2,
    },
  },

  craft_steel_ingot: {
    id: 'craft_steel_ingot',
    name: 'Steel Ingot',
    icon: '🔩',
    category: 'materials',
    tier: 2,
    description: 'Refined steel for smithing.',
    cost: {
      resources: { iron: 100, coal: 50, gold: 100 },
      materials: { ironOre: 25 },
    },
    output: {
      type: 'material',
      itemId: 'steelIngot',
      quantity: 3,
    },
    requiresTech: 'metallurgy',
  },

  craft_bone_dust: {
    id: 'craft_bone_dust',
    name: 'Bone Dust',
    icon: '💀',
    category: 'materials',
    tier: 2,
    description: 'Ground bones used in alchemy.',
    cost: {
      resources: { gold: 50 },
      materials: { bone: 50 },
    },
    output: {
      type: 'material',
      itemId: 'boneDust',
      quantity: 5,
    },
  },

  // ===========================================
  // TIER 3 - Processed Materials (90% quality / 2m)
  // ===========================================
  craft_enchanted_essence: {
    id: 'craft_enchanted_essence',
    name: 'Enchanted Essence',
    icon: '✨',
    category: 'materials',
    tier: 3,
    description: 'Concentrated magical energy for enchanting.',
    cost: {
      resources: { magic: 150, gold: 300 },
      materials: { essence: 50, crystal: 15 },
    },
    output: {
      type: 'material',
      itemId: 'enchantedEssence',
      quantity: 1,
    },
    requiresTech: 'arcane_arts',
  },

  craft_hardened_steel: {
    id: 'craft_hardened_steel',
    name: 'Hardened Steel',
    icon: '⚙️',
    category: 'materials',
    tier: 3,
    description: 'Super-strong steel alloy.',
    cost: {
      resources: { steel: 100, coal: 75, gold: 250 },
      materials: { darkSteel: 25 },
    },
    output: {
      type: 'material',
      itemId: 'hardenedSteel',
      quantity: 2,
    },
    requiresTech: 'blacksmithing',
  },

  craft_demon_essence: {
    id: 'craft_demon_essence',
    name: 'Demon Essence',
    icon: '😈',
    category: 'materials',
    tier: 3,
    description: 'Extracted demonic power.',
    cost: {
      resources: { magic: 200, gold: 400 },
      materials: { demonHorn: 15, sulfur: 25, essence: 25 },
    },
    output: {
      type: 'material',
      itemId: 'demonEssence',
      quantity: 1,
    },
    requiresTech: 'arcane_arts',
  },

  // ===========================================
  // TIER 4 - Rare Materials (100% quality / 4m)
  // ===========================================
  craft_void_crystal: {
    id: 'craft_void_crystal',
    name: 'Void Crystal',
    icon: '🌑',
    category: 'materials',
    tier: 4,
    description: 'Crystallized void energy for epic crafting.',
    cost: {
      resources: { magic: 500, crystals: 50 },
      materials: { voidFragment: 15, soulShard: 10 },
    },
    output: {
      type: 'material',
      itemId: 'voidCrystal',
      quantity: 1,
    },
    requiresTech: 'planar_theory',
  },

  craft_dragon_alloy: {
    id: 'craft_dragon_alloy',
    name: 'Dragon Alloy',
    icon: '🐉',
    category: 'materials',
    tier: 4,
    description: 'A metal infused with dragon essence.',
    cost: {
      resources: { steel: 150, gold: 1000, gems: 25 },
      materials: { dragonScale: 10, dragonHeart: 5 },
    },
    output: {
      type: 'material',
      itemId: 'dragonAlloy',
      quantity: 2,
    },
    requiresTech: 'artifact_crafting',
  },

  // ===========================================
  // TIER 5 - Legendary Materials (110% quality / 8m)
  // ===========================================
  craft_cosmic_shard: {
    id: 'craft_cosmic_shard',
    name: 'Cosmic Shard',
    icon: '🌟',
    category: 'materials',
    tier: 5,
    description: 'A fragment of cosmic power.',
    cost: {
      resources: { magic: 1000, crystals: 125, gold: 2500 },
      materials: { cosmicFragment: 25, starMetal: 15, divineEssence: 10 },
    },
    output: {
      type: 'material',
      itemId: 'cosmicShard',
      quantity: 1,
    },
    requiresTech: 'cosmic_awareness',
  },

  craft_eternal_ingot: {
    id: 'craft_eternal_ingot',
    name: 'Eternal Ingot',
    icon: '♾️',
    category: 'materials',
    tier: 5,
    description: 'Metal that transcends time itself.',
    cost: {
      resources: { steel: 250, gold: 5000, crystals: 150 },
      materials: { eterniumIngot: 10, cosmicOre: 15, voidEssence: 15 },
    },
    output: {
      type: 'material',
      itemId: 'eternalIngot',
      quantity: 1,
    },
    requiresTech: 'eternal_materials',
  },
};

export default MATERIAL_RECIPES;
